import {ChangeEvent, FC, useContext, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useSetRecoilState} from 'recoil';

import {Box, Button, FormControlLabel, Stack, Switch, Tooltip} from '@mui/material';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {ConfirmAction} from '../../../modals/ConfirmAction';
import {ModalContainer} from '../../../modals/ModalContainer';

import {EventBusContext} from '../../../../providers/EventBus';

import {DictionaryService} from '../../../../api/services/dictionary.service';

import {
  reasonRejectionsDictQueryByCodeTrigger,
  reasonRejectionsDictQueryTrigger,
} from '../../../../store/reasonRejections/atoms';

import {
  IReasonRejectionsDict,
  IReasonRejectionsDictPostBody,
} from '../../../../shared/types/reasonRejectionsDict.types';

import {green} from '@mui/material/colors';

interface IReasonRejectionForm {
  reasonRejection: IReasonRejectionsDict;
}

export const ReasonRejectionForm: FC<IReasonRejectionForm> = ({reasonRejection}) => {
  const triggerReasonRejectionsQuery = useSetRecoilState(reasonRejectionsDictQueryTrigger);
  const triggerReasonRejectionQueryByCode = useSetRecoilState(reasonRejectionsDictQueryByCodeTrigger);
  const {
    control,
    handleSubmit,
    formState: {isDirty, defaultValues},
    trigger,
    setValue,
  } = useForm<IReasonRejectionsDictPostBody>({
    defaultValues: {
      name: reasonRejection.name,
      code: reasonRejection.code,
      description: reasonRejection.description,
    },
  });
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const {bus} = useContext(EventBusContext);

  const toggleModalToDelete = () => {
    setOpen(!open);
  };

  const onSubmit: SubmitHandler<IReasonRejectionsDictPostBody> = async (data) => {
    setLoading(true);

    try {
      await DictionaryService.updateReasonRejection(reasonRejection.code, {...reasonRejection, ...data});

      triggerReasonRejectionQueryByCode((v) => v + 1);
      triggerReasonRejectionsQuery((v) => v + 1);
    } catch (err: any) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (setLoading: (bool: boolean) => void) => {
    setLoading(true);

    try {
      await DictionaryService.deleteReasonRejection(reasonRejection.code);

      triggerReasonRejectionsQuery((v) => v + 1);
      setTimeout(() => {
        navigate('/dictionaries/reasonRejections');
      }, 0);
    } catch (err: any) {
      bus?.emit('error', [err]);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleInputMode = (e: ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked);
  };

  const handleCancelInputMode = () => {
    setChecked(false);

    Object.entries({...defaultValues}).forEach((item) =>
      setValue(item[0] as keyof IReasonRejectionsDictPostBody, item[1]),
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{mt: 1.5}} direction="column" spacing={3}>
        <Stack direction="row">
          <FormControlLabel
            control={<Switch checked={checked} onChange={handleToggleInputMode} />}
            label="Редактировать"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Tooltip title={reasonRejection.name}>
            <Box>
              <ControlledInput
                control={control}
                name="name"
                disabled={!checked || loading}
                label="Название"
                placeholder="Название"
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title={reasonRejection.code}>
            <Box>
              <ControlledInput control={control} name="code" size="small" label="Код причины отмены" disabled />
            </Box>
          </Tooltip>
        </Stack>
        <ControlledInput
          control={control}
          name="description"
          disabled={!checked || loading}
          rules={{
            maxLength: {
              value: 400,
              message: 'Максимум 400 символов',
            },
          }}
          label="Комментарий"
          multiline
          rows={4}
          additionalFunc={() => {
            trigger('description');
          }}
        />
        <Stack direction="row" spacing={3} sx={{justifyContent: 'flex-end', mb: '12px !important'}}>
          {checked ? (
            <>
              <Button disabled={loading} onClick={handleCancelInputMode} variant="outlined" color="error">
                Отменить
              </Button>
              <LoadingButton
                sx={{
                  backgroundColor: green[500],
                  '&:hover': {
                    backgroundColor: green[600],
                  },
                }}
                type="submit"
                loading={loading}
                variant="contained"
                color="success"
                disabled={!isDirty}
              >
                Сохранить
              </LoadingButton>
            </>
          ) : (
            <Button onClick={toggleModalToDelete} variant="contained" color="error">
              Удалить
            </Button>
          )}
        </Stack>
        {open && (
          <ModalContainer
            sideEffect={handleDelete}
            renderModal={(sideEffect, loading) => (
              <ConfirmAction
                loading={loading}
                open={open}
                handleClose={toggleModalToDelete}
                handleSideEffect={sideEffect}
                title="Подтвердите удаление"
                buttonTitle="Удалить"
              />
            )}
          />
        )}
      </Stack>
    </form>
  );
};
